import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Accordion } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AREA_ICON, type ActionArea } from '../../controlCenterMeta';

type CatalogAccordionItemProps = {
    area: ActionArea;
    count: number;
    children: React.ReactNode;
};

/**
 * One area of the catalog, in its **compact** shape: a disclosure row that
 * opens its list beneath it.
 *
 * A twenty-eight-row grid never works at 390px, so the seven areas collapse to
 * seven rows and a reader opens the one they want. HeroUI's `Accordion` owns
 * the trigger, the panel, the animation, the separator and the `aria-expanded`
 * / `aria-controls` pair — this file only says what goes in the row.
 */
export const CatalogAccordionItem: React.FC<CatalogAccordionItemProps> = ({
    area,
    count,
    children,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Accordion.Item
            id={area}
            {...getNodeTestIdProp(`control-center-catalog-area-${area}`)}
        >
            <Accordion.Heading>
                <Accordion.Trigger
                    {...getNodeTestIdProp(
                        `control-center-catalog-area-toggle-${area}`,
                    )}
                >
                    <Icon
                        aria-hidden
                        className="me-2.5 size-4 shrink-0 text-muted"
                        icon={AREA_ICON[area]}
                    />
                    <span className="min-w-0 flex-1 truncate text-start">
                        {t(`controlCenter.areas.${area}`)}
                    </span>
                    <span className="shrink-0 text-xs text-muted">{count}</span>
                    <Accordion.Indicator />
                </Accordion.Trigger>
            </Accordion.Heading>
            <Accordion.Panel>
                <Accordion.Body>
                    <ul
                        className="grid gap-y-0.5"
                        {...getNodeTestIdProp(
                            `control-center-catalog-list-${area}`,
                        )}
                    >
                        {children}
                    </ul>
                </Accordion.Body>
            </Accordion.Panel>
        </Accordion.Item>
    );
};
